import * as _ from 'underscore';
import jsonpatch from 'fast-json-patch';
import { diffUtil, urlUtil, util } from '../../util';
import { HttpDataService } from '../../http/http-data.service';
import { FormattingService } from '../formatting.service';
import { AuthDataService, IAccessTypes, IAuthenticateResponse, ISecurityRole, IUserData, Permission } from './auth-data.service';
import { IExtendedWindow, IObject, IStateData } from '@type_definitions/datatypes';
import { ConnectionService } from './connection.service';
import { UserDataService } from '../../user/user-data.service';
import { BroadcastChannel } from 'broadcast-channel';
import { LanguageService } from '../../language/language.service';
import { Inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { TranslationService } from '../../translation/translation.service';
import { Transition } from '@uirouter/angular';
import { CookieService } from 'ngx-cookie-service';
import { GaDeferredService, IGADeferred } from '../ga-deferred.service';
import { DialogBusyButtonFn, GaMessageService, IDialogButton, MessageType } from '../../message/ga-message.service';
import { GaWebStorageService } from '../storage/ga-web-storage.service';
import { LogLevel, RemoteLoggerDataService } from '../logger/remote-logger-data.service';
import { URL_PRIVACY_STATEMENT } from '../../browser/url-collections';

export interface ILoginDetails {
  username:string;
  remember:boolean;
}

export interface IAccessPermissions {
  // role -> viewpermissions
  [ key:string ]:IViewPermissions;
}

export interface IViewPermissions {
  // view -> permission
  [ key:string ]:IViewPermission;
}

interface IViewPermission {
  read:boolean;
  write:boolean;
  special:boolean;
}

export interface IStateAndParams {
  stateName:string;
  stateParams?:any;
}

const DEFAULT_REMEMBER:boolean = true;
const DEFAULT_SKIN:string = 'acsl';
const KEY_REMEMBER:string = 'ga2.remember';
const KEY_LOGIN:string = 'ga2.login';
const KEY_TOKEN:string = 'ga2.token';
const KEY_ALTERNATE:string = 'ga2.alternate';
const KEY_SKIN:string = 'ga2.skin';
const LOGIN_FAILURE:string = 'Login failed';

enum UserType {
  ACSL,
  CUSTOMER,
  VENDOR,
  PARTNER,
  LAMPARTNER
}

const MESSAGE_I_AM_LOGGED_ON:string = 'I_AM_LOGGED_ON';
const MESSAGE_IS_LOGGED_ON:string = 'IS_LOGGED_ON';
const MESSAGE_LOGON:string = 'LOGON';
const CHECK_LOGON_TIMEOUT:number = 2 * 1000;

/**
 * @ngdoc service
 * @name ga2.shared.services.auth:authService
 * @requires ga2.shared.services.auth:messageService
 *
 * @description A service for interacting with the servers authentication subsystem.
 *
 * broadcasts the following messages:
 *
 *  - authenticated
 *  - authenticationFailure
 *
 */

@Injectable()
export class AuthService {
  public static STATE_APPROVED:string = 'APPROVED';
  public static STATE_WAITING_APPROVAL:string = 'WAIT_APRVL';
  public static STATE_REJECTED:string = 'REJECTED';
  public static STATE_REGISTERED:string = 'REGISTERED';

  private _alternateUserId:number;
  private _token:string;

  private authenticatedSubject:Subject<boolean> = new Subject<boolean>();
  private authFailureSubject:Subject<string> = new Subject<string>();
  private viewDataChangeSubject:Subject<IStateData> = new Subject<IStateData>();
  private permissionsSelectedSubject:Subject<IViewPermissions> = new Subject<IViewPermissions>();
  private currentUserUpdatedSubject:Subject<IUserData> = new Subject<IUserData>();
  private reloadUISubject:Subject<void> = new Subject<void>();

  public onAuthenticated:Observable<boolean> = this.authenticatedSubject.asObservable();
  public onAuthFailure:Observable<string> = this.authFailureSubject.asObservable();
  public onViewDataChange:Observable<IStateData> = this.viewDataChangeSubject.asObservable();
  public onPermissionsSelected:Observable<IViewPermissions> = this.permissionsSelectedSubject.asObservable();
  public onCurrentUserUpdated:Observable<IUserData> = this.currentUserUpdatedSubject.asObservable();
  public onReloadUI:Observable<void> = this.reloadUISubject.asObservable();

  private authenticating:boolean = false;
  private _autoAuthenticating:boolean;
  private authenticated:boolean = false;

  private currentUser:IUserData;
  private currentUserOld:IUserData;

  private stateAfterLogin:IStateAndParams;

  private authData:{
    login:string;
    alternateUserId:number;
    token:string;
    remember?:boolean;
  };

  private accessPermissions:IAccessPermissions = {};
  private viewPermissions:IViewPermissions = {};
  private permSelected = false;
  private viewData:IStateData = {};
  private logonMessageChannel:BroadcastChannel = new BroadcastChannel('GA2-LOGON');

  constructor(
    @Inject(Window) private window:IExtendedWindow,
    private httpDataService:HttpDataService,
    private webStorage:GaWebStorageService,
    private messageService:GaMessageService,
    private formattingService:FormattingService,
    private authDataService:AuthDataService,
    private connectionService:ConnectionService,
    private languageService:LanguageService,
    private translationService:TranslationService,
    private userDataService:UserDataService,
    private cookieService:CookieService,
    private deferredService:GaDeferredService,
    private remoteLoggerDataService:RemoteLoggerDataService
  ) {
    this.listen();
  }

  private listen():void {
    const messageHandler = (message:any):void => {
      // when another tab asks if we're logged on => check and send message if true
      if (message.message === MESSAGE_IS_LOGGED_ON && this.isAuthenticated()) {
        this.logonMessageChannel.postMessage({
          message: MESSAGE_I_AM_LOGGED_ON
        });
      } else if (message.message === MESSAGE_LOGON) {
        // another tab has sent a username => check if we're the same person
        if (this.isAuthenticated() && this.currentUser && this.currentUser.login !== message.data.login) {
          const msg:string = `logged on as someone else: ${this.currentUser.login} => ${message.data.login}`;
          this.remoteLoggerDataService.remoteLog(msg, LogLevel.WARN);
          console.warn(msg);
        }
      }
    };
    this.logonMessageChannel.addEventListener('message', messageHandler);
  }

  public get alternateUserId():number {
    return this._alternateUserId;
  }

  public set alternateUserId(alternateUserId:number) {
    this._alternateUserId = alternateUserId;
  }

  public authenticatedUrl(url:string):string {
    const params:IObject = {
      token: this.getToken() || ''
    };
    if (this.alternateUserId) {
      params.alternateUserId = this.alternateUserId;
    }
    return urlUtil.addQueryParams(url, params);
  }

  public activate(noAutoAuthentication?:boolean):Promise<void> {
    //first stab: load the token / auth data from the url
    this.loadAuthDataFromUrl();
    if (this.authData) {
      this.saveAuthData(false, this.authData.login, this.authData.alternateUserId, this.authData.token);
    }

    //second try: load from session/local storage
    if (!this.authData) {
      this.loadAuthDataFromStorage();
    }

    if (noAutoAuthentication) { //don't try to auto-authenticate -> delete the token that might be in storage
      this.setAuthenticated(false);
      this.setAuthenticating(false);
      this.deleteToken();
    } else if (!this.authData.token) { //no credentials found
      this.setAuthenticated(false);
      this.setAuthenticating(false);
    } else { //load current user -> this will effectively authenticate the token
      return this.authenticateAgainstServer();
    }

    return Promise.resolve();
  }

  public authenticateAgainstServer():Promise<void> {
    this._autoAuthenticating = true;
    this.setAuthenticated(false, true);
    this.setAuthenticating(true);

    const authMessage = this.messageService.showStatusMessage('Authenticating ...');

    let user:IUserData;
    return this.authDataService.loadCurrentUser()
      .then((u:IUserData) => {
        user = u;
        return this.createNewSession();
      })
      .then(() => {
        this.loadCurrentUserFromServerSuccess(user);
        return Promise.resolve();
      })
      .then(() => this.connectionService.startPolling())
      .catch((status:number) => {
        if (status === 403) { //Forbidden
          //we do not show an authentication failure to the user as this might confuse him/her
          console.warn('Auto-authentication failed');
          //clear out the token
          this.deleteToken();
          this.setAuthenticated(false);
        } else {
          const prefix:string = 'Server error';
          // noinspection HtmlUnknownAnchorTarget
          const suffix:string = status + '<br><a href="" onclick="document.location.reload();return false;">Reload</a> or <a href="#/login">Login</a>';
          this.messageService.showResponseErrorMessage(prefix, suffix);
        }
      })
      .finally(() => {
        this._autoAuthenticating = false;
        this.setAuthenticating(false);
        this.messageService.closeMessage(authMessage);
      });
  }

  private loadAuthDataFromUrl():void {
    const token:string = this.urlParams?.acslToken || this.urlParams?.token;
    if (!token) {
      return;
    }

    console.debug('Load auth data from URL');
    this.setToken(token);
    this.authData = {
      login: this.getLoginFromToken(token),
      token,
      alternateUserId: undefined
    };
  }

  private getLoginFromToken(token:string):string {
    if (!token) {
      return;
    }

    const decoded = util.atob(token);
    if (!decoded) {
      console.error('Unable to decode token from url: ' + token);
      return;
    }

    if (!decoded.match(/.+:\d+:.+/)) {
      console.error('Decoded token has a wrong format: ' + decoded);
      return;
    }

    return decoded.split(':')[ 0 ];
  }

  private deleteToken():void {
    this.authData.token = undefined;
    this._token = undefined;
    this.webStorage.session.remove(KEY_TOKEN);
    this.webStorage.local.remove(KEY_TOKEN);
  }

  private deleteLoginIfNecessary():void {
    if (!this.authData.remember) {
      this.authData.login = undefined;
      this.webStorage.session.remove(KEY_LOGIN);
    }
  }

  private saveAuthData(remember:boolean, login:string, alternateUserId?:number, token?:string):void {
    if (util.isNullOrUndefined(remember)) { //this is undefined when loading a token from url
      remember = this.webStorage.local.get(KEY_REMEMBER);
      if (util.isNullOrUndefined(remember)) {
        remember = DEFAULT_REMEMBER;
      }
    }

    this.authData = {
      login,
      token,
      remember,
      alternateUserId
    };

    //remove from session and localstorage
    this.webStorage.session.remove(KEY_LOGIN);
    this.webStorage.session.remove(KEY_TOKEN);
    this.webStorage.session.remove(KEY_ALTERNATE);

    this.webStorage.local.remove(KEY_LOGIN);
    this.webStorage.local.remove(KEY_TOKEN);
    this.webStorage.local.remove(KEY_REMEMBER);

    //add login and token to local and/or session (depending on remember)
    if (remember && this.authData.login) {
      this.webStorage.local.set(KEY_LOGIN, this.authData.login);
    }

    if (token) {
      this.webStorage.session.set(KEY_TOKEN, token);
      if (remember) {
        this.webStorage.local.set(KEY_TOKEN, token);
      }
    }
    this._token = token;

    if (alternateUserId) {
      this.webStorage.session.set(KEY_ALTERNATE, alternateUserId);
    }

    //always add remember to localstorage
    this.webStorage.local.set(KEY_REMEMBER, remember);

    //now sync with authData
    this.loadAuthDataFromStorage();
  }

  private loadAuthDataFromStorage():void {
    this.authData = {
      login: this.webStorage.session.get(KEY_LOGIN) || this.webStorage.local.get(KEY_LOGIN) || this.authData?.login || this.cookieService.get('Username'),
      token: this.webStorage.session.get(KEY_TOKEN) || this.webStorage.local.get(KEY_TOKEN) || this.authData?.token || this.cookieService.get('Token'),
      alternateUserId: this.webStorage.session.get(KEY_ALTERNATE) || this.cookieService.get('AlternateUser')
    };

    this.authData.login = this.authData.login || this.getLoginFromToken(this.authData.token);

    const remember = this.webStorage.local.get(KEY_REMEMBER);
    this.authData.remember = util.isNullOrUndefined(remember)? DEFAULT_REMEMBER : remember;

    this._token = this.authData.token;
    this.alternateUserId = this.authData.alternateUserId;
  }

  public setAuthenticated(auth:boolean, noBroadcast?:boolean):void {
    this.authenticated = auth;
    if (!noBroadcast) {
      this.authenticatedSubject.next(this.authenticated);
    }
  }

  public isAuthenticated():boolean {
    return this.authenticated;
  }

  private setAuthenticating(auth:boolean):void {
    this.authenticating = auth;
  }

  public isAuthenticating():boolean {
    return this.authenticating;
  }

  private broadcastAuthFailure(msg:string):void {
    this.authFailureSubject.next(msg);
  }

  private loadCurrentUserFromServerSuccess = (user:IUserData):void => {
    // notify who we are to other tabs
    this.logonMessageChannel.postMessage({
      message: MESSAGE_LOGON,
      data: {
        login: user.login
      }
    });
    this.currentUser = this.processUser(user);
    this.accessPermissions = this.processAccessTypes(this.currentUser.accessTypes);
    this.currentUser.accessTypes = undefined;
    this.currentUserOld = util.copyAndExtend(this.currentUser);
    this.saveSkinCode(this.currentUser.skinCode);
    this.setAuthenticated(true);
    this.setAuthenticating(false);
    this.remoteLoggerDataService.logBrowserDetails();
  };

  public performLogin(username:string, password:string, remember:boolean, asUser?:string):void {
    this.saveAuthData(remember, username);

    console.info('login: ' + username);
    this.setAuthenticating(true);

    const authFailure = (reason?:string):void => {
      this.setAuthenticated(false);
      this.setAuthenticating(false);

      if (!reason) {
        reason = LOGIN_FAILURE;
      } else if (!isNaN(<any>reason)) { // numeric -> http status code
        reason = LOGIN_FAILURE + ` (${reason})`;
      }

      this.broadcastAuthFailure(reason);
    };

    this.authDataService.authenticate(username, password, asUser)
      .then((authResponse:IAuthenticateResponse) => {
        this.saveAuthData(remember, username, authResponse.alternateUserId, authResponse.token);
        let user:IUserData;
        return this.authDataService.loadCurrentUser()
          .then((u:IUserData) => {
            user = u;
            return this.checkPrivacyStatement(user, asUser);
          })
          .then(() => {
            return this.createNewSession();
          })
          .then(() => {
            this.loadCurrentUserFromServerSuccess(user);
          })
          .then(() => {
            this.connectionService.startPolling();
          })
          // .catch(authFailure)
          .finally(() => {
            this.setAuthenticating(false);
          });
      })
      .catch(authFailure);
  }

  public performLogout():Promise<any> {
    console.info('logout');
    //destroy the session on GA1
    return this.authDataService.destroySession()
      .then(() => {
        this.deleteToken();
        this.deleteLoginIfNecessary();
        this.setAuthenticated(false);
        this.stateAfterLogin = null;
      })
      .then(() => this.connectionService.stopPolling());
  }

  public getLoginDetails():ILoginDetails {
    return {
      username: this.authData.login,
      remember: this.authData.remember
    };
  }

  private processUser(user:IUserData):IUserData {
    user.name = this.formattingService.formatUsername(user);
    return user;
  }

  private processAccessTypes(accessTypes:IAccessTypes):IAccessPermissions {
    const accessPermissions:IAccessPermissions = {};
    _.each(accessTypes, (securityRole:ISecurityRole, roleName) => {
      const viewPermissions:IViewPermissions = {};
      accessPermissions[ roleName ] = viewPermissions;
      _.each(securityRole, (permission:Permission, securityObject:string) => {
        switch (permission) {
          case 'S':
            viewPermissions[ securityObject ] = {
              special: true,
              write: true,
              read: true
            };
            break;
          case 'W':
            viewPermissions[ securityObject ] = {
              special: false,
              write: true,
              read: true
            };
            break;
          case 'R':
            viewPermissions[ securityObject ] = {
              special: false,
              write: false,
              read: true
            };
            break;
          default:
            viewPermissions[ securityObject ] = {
              special: false,
              write: false,
              read: false
            };
        }
      });
    });

    return accessPermissions;
  }

  public selectPermissions(role?:string) {
    this.permSelected = true;
    if (!role) {
      this.viewPermissions = {};
    } else {
      this.viewPermissions = this.accessPermissions[ role ] || {};
    }
    this.permissionsSelectedSubject.next(this.viewPermissions);
  }

  public viewSpecial(viewCode?:string):boolean {
    const viewPermission = this.viewPermissions[ this.vc(viewCode) ];
    return viewPermission? viewPermission.special : false;
  }

  public viewRead(viewCode?:string):boolean {
    const viewPermission = this.viewPermissions[ this.vc(viewCode) ];
    return (viewPermission? viewPermission.read : false);
  }

  public viewWrite(viewCode?:string):boolean {
    const viewPermission = this.viewPermissions[ this.vc(viewCode) ];
    return viewPermission? viewPermission.write : false;
  }

  public permissionsSelected():boolean {
    return this.permSelected;
  }

  public currentViewData():IStateData {
    return this.viewData;
  }

  private vc(viewCode:string):string {
    return viewCode || (this.viewData && this.viewData.viewCode) || '';
  }

  public hasViewPermission(viewPrefix:string):boolean {
    return util.notNullOrUndefinedOrEmpty(_.find(this.viewPermissions,
      (permission:IViewPermissions, viewCode:string) => {
        return viewCode === viewPrefix || util.stringStartsWith(viewCode, `${viewPrefix}-`);
      }));
  }

  public captureViewData = (transition:Transition):void => {
    this.viewData = transition.to()?.data || {};
    this.viewDataChangeSubject.next(this.viewData);
  };

  public checkAcslUser():boolean {
    if (this.currentUser) {
      return this.currentUser.userType === UserType[ UserType.ACSL ];
    }
  }

  public checkCustomersUser():boolean {
    if (this.currentUser) {
      return this.currentUser.userType === UserType[ UserType.CUSTOMER ];
    }
  }

  public checkVendorUser():boolean {
    if (this.currentUser) {
      return this.currentUser.userType === UserType[ UserType.VENDOR ];
    }
  }

  public checkPartnerUser():boolean {
    if (this.currentUser) {
      return this.currentUser.userType === UserType[ UserType.PARTNER ] || this.currentUser.userType === UserType[ UserType.LAMPARTNER ];
    }
  }

  public patchCurrentUser():Promise<any> {
    const patchData = jsonpatch.compare(this.currentUserOld, this.currentUser);
    const patch = diffUtil.createPatch(patchData);

    const url = 'rest/userman/userprofile/' + this.currentUser.id;
    return this.httpDataService.doPATCH(
      url,
      undefined,
      patch,
      undefined,
      'could not update user'
    ).then(() => {
      this.currentUserOld = util.copyAndExtend(this.currentUser);
    });
  }

  public getCurrentUser():IUserData {
    return this.currentUser;
  }

  public setCurrentUser(currentUser:IUserData) {
    this.currentUser = currentUser;
    this.saveSkinCode(this.currentUser.skinCode);
    this.languageService.saveLanguage(this.currentUser.language && this.currentUser.language.isoCode);
    this.currentUserUpdatedSubject.next(currentUser);
  }

  public setStateAfterLogin(state:IStateAndParams) {
    this.stateAfterLogin = state;
  }

  public getStateAfterLogin():IStateAndParams {
    return this.stateAfterLogin;
  }

  private saveSkinCode(skinCode:string) {
    if (util.isNullOrUndefined(skinCode)) {
      skinCode = DEFAULT_SKIN;
    }
    this.webStorage.local.set(KEY_SKIN, skinCode);
  }

  public getSkinCode():string {
    let skinCode:string = this.cookieService.get('Theme');
    if (!skinCode) {
      skinCode = this.webStorage.local.get(KEY_SKIN);
    }
    return skinCode;
  }

  /**
   * @ngdoc method
   * @name ga2.shared.services.auth:authService#getToken
   * @methodOf ga2.shared.services.auth:authService
   *
   * @description Get the authentication token
   *
   * @returns The authentication token that is currently used
   */
  public getToken():string {
    return this._token;
  }

  /**
   * @ngdoc method
   * @name ga2.shared.services.auth:authService#setToken
   * @methodOf ga2.shared.services.auth:authService
   *
   * @description Changes the authentication token (and changes the login username)
   *
   * @param {string} token - the new token to use for authentication
   * @param {boolean} remember - remember user
   */
  public setToken(token:string, remember?:boolean):void {
    const login:string = this.getLoginFromToken(token);
    this.saveAuthData(remember, login, this.authData && this.authData.alternateUserId, token);
  }

  /**
   * @ngdoc method
   * @name ga2.shared.services.auth:authService#setLogin
   * @methodOf ga2.shared.services.auth:authService
   *
   * @description Changes the login username and clears the current token
   *
   * @param {string} login - the new username to use when logging in
   */
  public setLogin(login:string) {
    this.saveAuthData(undefined, login);
  }

  public get autoAuthenticating():boolean {
    return this._autoAuthenticating;
  }

  public createNewSession(reload?:boolean):Promise<void> {
    return this.authDataService.createSession(this.urlParams?.punchoutMessageId, this.urlParams?.theme)
      .then(():void => {
        if (reload) {
          this.reloadUISubject.next();
        }
      });
  }

  private checkPrivacyStatement(user:IUserData, asUser:string):Promise<void> {
    if (!asUser && !user.privacyAgreed) {
      const deferred:IGADeferred<void> = this.deferredService.defer();

      this.messageService.showDialog({
        title: 'APP.ACCESS.PRIVACY_STATEMENT_TITLE',
        message: this.translationService.instant('APP.ACCESS.PRIVACY_STATEMENT_MESSAGE', {
          link: `<a href="${URL_PRIVACY_STATEMENT}" target="_blank">${this.translationService.instant('GA.GENERAL.PRIVACY_STATEMENT')}</a>`
        }),
        dialogClass: MessageType.WARNING,
        closeButton: true,
        buttons: <IDialogButton[]>[
          {
            label: 'GA.BUTTONS.ACCEPT',
            className: 'btn-success',
            onClick: (updateBusyButtonFn:DialogBusyButtonFn) => {
              updateBusyButtonFn(true);
              this.userDataService.acceptPrivacyStatement()
                .then(() => {
                  user.privacyAgreed = true;
                })
                .finally(() => {
                  updateBusyButtonFn(false);
                  deferred.resolve();
                });
            }
          }
        ],
        callback: () => deferred.resolve()
      });

      return deferred.promise.then(() => {
        if (user.privacyAgreed) {
          return Promise.resolve();
        } else {
          return Promise.reject(this.translationService.instant('GA.GENERAL.PRIVACY_STATEMENT_ERROR'));
        }
      });
    } else {
      return Promise.resolve();
    }
  }

  public isLoggedOnInOtherTab():Promise<boolean> {
    const deferred:IGADeferred<boolean> = this.deferredService.defer();

    const messageHandler = (message) => {
      // when another tab responds => return true
      if (message.message === MESSAGE_I_AM_LOGGED_ON) {
        this.logonMessageChannel.removeEventListener('message', messageHandler);
        clearTimeout(timer);
        deferred.resolve(true);
      }
    };
    this.logonMessageChannel.addEventListener('message', messageHandler);

    // timeout waiting for response => return false
    const timer = setTimeout(() => {
        this.logonMessageChannel.removeEventListener('message', messageHandler);
        deferred.resolve(false);
      },
      CHECK_LOGON_TIMEOUT);

    // send message with question to other tabs
    this.logonMessageChannel.postMessage({
      message: MESSAGE_IS_LOGGED_ON
    });

    return deferred.promise;

  }

  private get urlParams():IObject {
    const url:string = this.window.location.hash.substr(1, this.window.location.hash.length);
    return urlUtil.getQueryParams(url);
  }
}